import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import "./_modal.scss";
import iconClose from "./images/icon-close.svg";
import iconCloseBlack from "./images/icon-close-black.svg";
import { LayoutContext } from "../layout-context-container";

function PatientModal({
  showCloseButton = true,
  onOpen = undefined,
  onClose = undefined,
  ariaLabelledby = undefined, // provide id
  ariaDescribedby = undefined,  // provide id
  customClass = "",
  isHCP = false,
  children
}, forwardRef) {
  const containerRef = useRef(null);
  const {setModalState} = useContext(LayoutContext);
  const [stateOpen, setStateOpen] = useState(false);
  const [stateOpenDelayed, setStateOpenDelayed] = useState(false);
  const [init, setInt] = useState(false);
  const [refocus, setRefocus] = useState(false);

  const containerClass = useMemo(() => {
    var className = `patient-modal ${stateOpenDelayed ? " opacity-100" : " opacity-0"}`;
    if (customClass) {
      className = `${className} ${customClass}`;
    }
    if (isHCP) {
      className = `${className} patient-modal--is-hcp`;
    }
    return className;
  }, [customClass, stateOpenDelayed, isHCP]);

  useEffect(() => {
    if (stateOpen && onOpen) {
      onOpen(true);
    } else if (init && !stateOpen && onClose) {
      onClose(false);
    } else if (!init) {
      setInt(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOpen]);

  useEffect(() => {
    const root = document.documentElement;
    if (stateOpen) {
      setTimeout(() => setStateOpenDelayed(true), 150);
      root.style.position = "sticky";
      root.style.height = "100%";
      root.style.overflow = "hidden";
    } else {
      setStateOpenDelayed(false);
      root.style.position = "";
      root.style.height = "";
      root.style.overflow = "";
    }

  }, [stateOpen]);

  useEffect(() => {
    const modal = containerRef.current;
    let firstFocusableElement;
    let lastFocusableElement;
    
    if (modal) {
      const focusableElements = modal?.querySelectorAll('input, a[href], button, [tabindex]:not([tabindex="-1"])');
      firstFocusableElement = focusableElements[0];
      lastFocusableElement = focusableElements[focusableElements.length - 1];
    }

    function handeKeyDown(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement?.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement?.focus();
          }
        }
      }
    }

    if (setStateOpenDelayed || refocus) {
      // containerRef.current?.focus();

      firstFocusableElement?.focus();
      modal?.addEventListener('keydown', handeKeyDown);

      setRefocus(false);
    }
    
    return () => !stateOpenDelayed && modal?.addEventListener('keydown', handeKeyDown);

  }, [stateOpenDelayed, refocus]);

  useImperativeHandle(
    forwardRef,
    () => ({
      open() {
        setStateOpen(true);
      },
      close() {
        setStateOpen(false);
      },
      get state() {
        return stateOpen;
      },
      refocus() {
        setRefocus(true);
      }
    }),
    [stateOpen]
  );

  return (
    <>
      {/* modal */}
      {stateOpen && (
        <div
          ref={containerRef}
          className={containerClass}
          onClick={(e) => {
            if (e.target === containerRef.current) {
              e.stopPropagation();
              setStateOpen(false);
              setModalState(false);
            }
          }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
        >
          {/* modal content */}
          <div className={`patient-modal__content${stateOpenDelayed ? " opacity-100" : " opacity-0"}`}>
            <div className="patient-modal__content__body">
              {children}
              {showCloseButton ? (
                <button 
                  className="patient-modal__close"
                  onClick={() => {setStateOpen(false); setModalState(false);}}
                  data-gtm-event-name="Recruitment Form Input"
                  data-gtm-event-category="Form Input"
                  data-gtm-event-action="Click"
                  data-gtm-event-label="Close Icon"
                >
                  <img loading="lazy" src={isHCP ? iconCloseBlack : iconClose} width={37} alt="close icon" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default forwardRef(PatientModal);